import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import Button from '../button/Button';
import Icon from '../icon/Icon';
import ConfirmationModal from '../../general/confirmation_modal/ConfirmationModal';
import { getCSRFToken } from 'lib/request';

const styles = require("./DocumentUploader.module.scss");

export type DocumentSectionProps = {
  className?: string;
  deleteUrl?: string;
  formName?: string;
  id: string;
  isSelected?: boolean;
  name: string;
  onDelete?: (id: string) => void;
  onSelect?: (id: string) => void;
  previewUrl?: string;
  uploadDate: string;
};

export default function DocumentSection({
  className,
  deleteUrl,
  formName,
  id,
  isSelected = false,
  name,
  onDelete,
  onSelect,
  previewUrl,
  uploadDate,
}: DocumentSectionProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsModalOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteUrl) {
      try {
        const response = await fetch(deleteUrl, {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCSRFToken(),
            'Accept': 'application/json',
          },
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            onDelete?.(id);
          }
        }
      } finally {
        setIsModalOpen(false);
      }
    } else {
      onDelete?.(id);
      setIsModalOpen(false);
    }
  }, [deleteUrl, id, onDelete]);

  const handleSelection = useCallback(() => {
    onSelect?.(id);
  }, [id, onSelect]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onSelect?.(id);
    }
  }, [id, onSelect]);

  const containerClassName = classNames(
    styles.documentWrapper,
    { [styles.selected]: isSelected },
    className
  );

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        className={containerClassName}
        onClick={handleSelection}
        onKeyDown={handleKeyDown}
        aria-label={`${isSelected ? 'Selected:' : 'Select'} ${name}`}
        aria-pressed={isSelected}
      >
        {formName && (
          <input
            type="radio"
            name={formName}
            value={id}
            checked={isSelected}
            onChange={handleSelection}
            className={styles.hiddenRadioInput}
            aria-label={`Select ${name}`}
          />
        )}

        <div className={styles.content}>
          <Icon
            name={isSelected ? "radioButtonChecked" : "radioButtonUnchecked"}
            size="md"
            color="talent"
            aria-hidden="true"
          />

          <div className={classNames(styles.fileInfo, styles.hasFile)}>
            <span className={styles.title}>{name}</span>
            <span className={styles.subtitle}>{uploadDate && `Uploaded on ${uploadDate}`}</span>
          </div>

          <div className={styles.actionButtons}>
            {previewUrl && (
              <Button
                type="terceary"
                size="S"
                iconLeft="eye"
                href={previewUrl}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Preview ${name}`}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              />
            )}
            {(onDelete || deleteUrl) && (
              <Button
                type="terceary"
                size="S"
                iconLeft="delete"
                aria-label={`Delete ${name}`}
                onClick={handleDelete}
              />
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isModalOpen}
        title="Delete resume from profile?"
        description="Upon deleting, this resume will no longer be available on your profile."
        onConfirm={handleConfirmDelete}
        onClose={() => setIsModalOpen(false)}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </>
  );
}
